import { Alert, AlertProps, List, ListItem } from '@mui/material';
import { RESPONSE_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import type { FetchErrorsType, ErrorData } from '@/types/types';
import { getErrorMessageText, JSXFromString } from '@/utils';
import { isArrayWithValues } from '@/utils/fp';

interface ErrorAlertProps extends Omit<AlertProps, 'errorMessage'> {
    id?: string;
    errorMessage?: FetchErrorsType | string;
}

export const ErrorAlert = ({ id, errorMessage, ...other }: ErrorAlertProps) => {
    const { t } = usePhrase();

    const handleErrorMessage = (err: ErrorData | string) => {
        if (typeof err === 'string') return JSXFromString(err);
        const message = getErrorMessageText(err, t);
        return JSXFromString(message);
    };

    const getSeverity = (err: ErrorData | string): AlertProps['severity'] => {
        if (typeof err !== 'string' && err.type) {
            return err.type as AlertProps['severity'];
        }
        return 'error';
    };

    if (typeof errorMessage === 'string') {
        return (
            <Alert severity={getSeverity(errorMessage)} id={id} {...other}>
                {handleErrorMessage(errorMessage)}
            </Alert>
        );
    }

    const generalErrors = errorMessage?.generalErrors;
    if (!generalErrors || !isArrayWithValues(generalErrors)) {
        return null;
    }

    const severity = generalErrors[0]?.type ?? 'error';

    return (
        <Alert data-testid="error-alert" severity={severity} id={id} {...other}>
            {generalErrors.length > 1 ? (
                generalErrors.filter((err) => 'phraseId' in err).length ? (
                    <List
                        sx={{
                            display: 'list',
                            color: 'inherit',
                            listStyleType: 'disc',
                            listStylePosition: 'inside',
                        }}
                    >
                        {generalErrors.map(
                            (err) =>
                                (err?.phraseId || (err?.message && err?.isMessageUserFriendly)) && (
                                    <ListItem
                                        sx={{ display: 'list-item', m: 0, p: 0 }}
                                        key={err.phraseId || err.message}
                                    >
                                        {handleErrorMessage(err)}
                                    </ListItem>
                                ),
                        )}
                    </List>
                ) : (
                    t(RESPONSE_ERROR_PHRASES.UNKNOWN)
                )
            ) : (
                handleErrorMessage(generalErrors[0])
            )}
        </Alert>
    );
};
